import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import R from "ramda";
import getDisplayName from "../utils/getDisplayName";
import convertAppPathPattern from "../utils/convertAppPathPattern";
import * as actionCreators from "../actions";
import { stepStatusMap } from "../constants/status";

const DEFAULT_STEP = "1";

const connectApplicationSteps = DataSource => WrappedComponent => {
  class ConnectApplicationSteps extends Component {
    constructor(props) {
      super(props);
      this.state = {
        startSaving: false,
        isReadyToSave: false,
        step: DEFAULT_STEP
      };
    }

    componentDidMount() {
      this.props.dispatchUpdateActiveStep(this.state.step);
    }

    static getDerivedStateFromProps(props, state) {
      const {
        match: {
          params: { step }
        }
      } = props;

      let nextStep = {};
      let nextStatus = {};
      if (step && step !== state.step) {
        nextStep = {
          step
        };
      }

      if (state.startSaving === true && R.not(R.isEmpty(props.save.data))) {
        nextStatus = {
          startSaving: false,
          isReadyToSave: true
        };
      } else if (state.isReadyToSave === true) {
        nextStatus = {
          isReadyToSave: false
        };
      }
      const nextState = {
        ...nextStep,
        ...nextStatus
      };
      return R.isEmpty(nextState) ? null : nextState;
    }

    componentDidUpdate(prevProps, prevState) {
      if (
        this.state.isReadyToSave &&
        prevState.isReadyToSave !== this.state.isReadyToSave
      ) {
        return this.requestSaveCards();
      }

      if (prevState.step !== this.state.step) {
        return this.props.dispatchUpdateActiveStep(this.state.step);
      }

      return false;
    }

    requestSaveCards = () => {
      const { save = {} } = this.props;
      DataSource.saveCards(save.data).then(
        response => {
          const { data } = response;
          this.props.dispatchUpdateSavedCards(data);
        },
        error => {
          const { response: status, statusText, data = {} } = error;
          this.props.dispatchUpdateSavedCards({
            code: status,
            msg: statusText,
            ...data
          });
        }
      );
      this.props.dispatchUpdateActiveStep(this.state.step);
    };

    convertPath = step => {
      const {
        match: { path }
      } = this.props;
      return convertAppPathPattern(path, null, step);
    };

    handleChange = step => {
      const nextPath = this.convertPath(step);
      this.props.history.push(nextPath);
    };

    handlePrevClick = () => {
      const { activeStep, history } = this.props;
      const step = parseInt(activeStep, 10) - 1;
      const nextPath = this.convertPath(step);
      history.push(nextPath);
    };

    handleNextClick = () => {
      const { activeStep, history } = this.props;
      const step = parseInt(activeStep, 10) + 1;
      const nextPath = this.convertPath(step);
      history.push(nextPath);
    };

    verifySavedCards = (cardsList = []) => {
      this.setState({ startSaving: true, isReadyToSave: false });
      this.props.dispatchVerifySavedCards({ savedCardsList: cardsList });
    };

    handleSave = (list = []) => {
      if (R.isEmpty(list)) {
        return false;
      }
      const {
        save: { cardsList, data }
      } = this.props;
      const stepStatus = R.pathOr(
        stepStatusMap.ERROR,
        ["step", "status"],
        data
      );
      const differentList = R.difference(list, cardsList);
      // check the cards list has been saved before, and the step whether is completed
      if (R.isEmpty(differentList) && stepStatus === stepStatusMap.COMPLETED) {
        return false;
      }

      return this.verifySavedCards(list);
    };

    handleSubmit = () => {
      const { applyId, dispatchUpdateSubmittedApplication } = this.props;
      DataSource.submitApplication(applyId).then(
        response => {
          const { data } = response;
          dispatchUpdateSubmittedApplication(data);
        },
        error => {
          const { response: status, statusText, data = {} } = error;
          dispatchUpdateSubmittedApplication({
            code: status,
            msg: statusText,
            ...data
          });
        }
      );
    };

    render() {
      const {
        stepsList = [],
        steps = {},
        activeStep,
        save = {},
        submit = {},
        ...restProps
      } = this.props;
      return (
        <WrappedComponent
          injectedProps={{
            stepsList,
            steps,
            activeStep,
            save,
            submit,
            isSubmittable: submit.isActive,
            handleChange: this.handleChange,
            handlePrevClick: this.handlePrevClick,
            handleNextClick: this.handleNextClick,
            verifySavedCards: this.verifySavedCards,
            handleSave: this.handleSave,
            handleSubmit: this.handleSubmit
          }}
          {...restProps}
        />
      );
    }
  }

  ConnectApplicationSteps.displayName = `ConnectApplicationSteps(${getDisplayName(
    WrappedComponent
  )})`;

  ConnectApplicationSteps.propTypes = {
    match: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    dispatchVerifySavedCards: PropTypes.func.isRequired,
    dispatchUpdateSavedCards: PropTypes.func.isRequired,
    dispatchUpdateSubmittedApplication: PropTypes.func.isRequired,
    stepsList: PropTypes.array,
    steps: PropTypes.object,
    activeStep: PropTypes.string,
    save: PropTypes.object,
    submit: PropTypes.object,
    applyId: PropTypes.number,
    dispatchUpdateActiveStep: PropTypes.func.isRequired
  };

  const mapStateToProps = ({ application }) => ({
    stepsList: application.stepsList,
    steps: application.steps,
    activeStep: application.activeStep,
    save: application.save,
    submit: application.submit,
    applyId: application.applyId
  });

  const mapDispatchToProps = {
    dispatchVerifySavedCards: actionCreators.verifySavedCards,
    dispatchUpdateSavedCards: actionCreators.updateSavedCards,
    dispatchUpdateSubmittedApplication:
      actionCreators.updateSubmittedApplication,
    dispatchUpdateActiveStep: actionCreators.updateActiveStep
  };

  return R.compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(
    ConnectApplicationSteps
  );
};

export default connectApplicationSteps;
